import React, { useState, useEffect } from "react";
import { Col, Row, Form } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import DatePicker from "react-datepicker";
import { Common, EmployeeIcons } from "../../assets/images";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setPettyCashChange } from "../../store/slices/commonSlice";
import { setAddCreditLoading } from "../../store/slices/pettyCashSlice";
// API Imports
import { useAddCreditMutation } from "../../services/PettyCash";
import { useUploadImageMutation } from "../../services/images-upload-service";
import { LoaderBlue, LoaderWhite } from "../Lotties";
import { ToastErrorComponent } from "../Toasts";
import { useNavigate } from "react-router-dom";
import Content from "../AccountCharts/get-all-accounts-api";
import { AccountChart } from "../../interfaces/account-charts";
import ApprovalsSet from "../Employees/approvals-set";

interface AddDebitCash {
  incomeType: string;
  incomeAmount: string;
  incomeDate: string;
  incomeDetail: string;
  accountNo: string;
}

const initialValues: AddDebitCash = {
  incomeType: "",
  incomeAmount: "",
  incomeDate: "",
  incomeDetail: "",
  accountNo: "",
};

const CreditPettyCash = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [attachment, setAttachment] = useState("");
  const [imageLoading, setImageLoading] = useState(false);
  const accessToken = useAppSelector((state) => state.common.accessToken);
  const userID = useAppSelector((state) => state.common.userID);
  const profileData = useAppSelector((state) => state.settings.profileData);
  const pettyCashChange = useAppSelector(
    (state) => state.common.pettyCashChange,
  );
  const accounts = useAppSelector((state) => state.accountCharts);
  const pettyCashState = useAppSelector((state) => state.pettyCash);
  const [show, setShow] = React.useState(false);
  const [errorContent, setErrorContent] = React.useState("");

  const [addCredit] = useAddCreditMutation();

  // Languages Sets
  const [languageSets, setLanguageSets] = useState<React.ReactNode[]>([]);
  const [languageData, setLanguageData] = useState<
    {
      uId: string;
      userId: string;
      date: string;
      name: string;
      role: string;
      status: string;
    }[]
  >([]);

  const handleAddLanguageSet = () => {
    setLanguageData([
      ...languageData,
      {
        uId: Math.random().toString(36).substring(7),
        userId: "",
        date: "",
        name: "",
        role: "",
        status: "Pending",
      },
    ]);
  };

  const handleRemoveLanguageSet = (index: string) => {
    const updatedInputData = languageData.filter(
      (item, i) => item.uId !== index,
    );
    setLanguageData(updatedInputData);
  };
  const handleLanguageInputChange = (
    index: string,
    data: {
      userId: string;
      name: string;
      role: string;
    },
  ) => {
    //  search by uniqueId and update the data
    const updatedInput = languageData.map((item) => {
      if (item.uId === index) {
        return {
          ...item,
          ...data,
        };
      }
      return item;
    });
    setLanguageData(updatedInput);
  };

  useEffect(() => {
    const updatedLanguageSets = languageData.map((data, index) => (
      <ApprovalsSet
        key={data.uId}
        data={data}
        custom="Yes"
        uniqueId={data.uId}
        onRemove={(key: string) => handleRemoveLanguageSet(key)}
        onInputChange={(inputData) =>
          handleLanguageInputChange(data.uId, inputData)
        }
      />
    ));
    setLanguageSets(updatedLanguageSets);
  }, [languageData]);
  const allUsers = useAppSelector((state) => state.settings.allUsers);
  const superadmin = allUsers.find((user) => user.role === "Super Admin");
  const userIds =
    languageData.length > 0 ? languageData.map((data) => data.userId) : [];

  const handleSubmit = async (values: AddDebitCash) => {
    dispatch(setAddCreditLoading(true));
    const date = values.incomeDate
      ? new Date(values.incomeDate).toLocaleDateString()
      : "";
    try {
      const data = {
        userID: userID || "",
        accessToken: accessToken || "",
        permission: profileData.permissions.addPettyCash || false,
        payload: {
          transactionDate: date,
          expenseType: values.incomeType,
          transactionDetail: values.incomeDetail,
          transactionImg: attachment,
          transactionAmount: values.incomeAmount,
          accountNo: values.accountNo,
          accountName:
            accounts.accountChartsList.find(
              (account) => account.accountId === values.accountNo,
            )?.accountName || "",
          link: "/petty-cash/ledger",
          ids: [superadmin?._id || "", ...userIds],
          creditApprovals: [
            {
              uId: Math.random().toString(36).substring(7),
              userId: superadmin?._id || "",
              date: "",
              name: superadmin?.firstName + " " + superadmin?.lastName || "",
              role: superadmin?.role || "",
              status: "Pending",
            },
            ...languageData,
          ],
        },
      };
      await addCredit(data).unwrap();
      dispatch(setPettyCashChange(!pettyCashChange));
      navigate("/petty-cash/ledger");
    } catch (e: any) {
      if (e) {
        window.scrollTo(0, 0);
        setErrorContent(e.data.data.message);
        setShow(true);
      }
    } finally {
      dispatch(setAddCreditLoading(false));
    }
  };

  // Upload Attachment
  const [uploadImage] = useUploadImageMutation();

  const handleUploadImage = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      setImageLoading(true);
      const file = event.target.files[0];
      const formData = new FormData();
      formData.append("myImage", file);

      const data = {
        accessToken: accessToken || "",
        permission: true,
        payload: formData,
      };

      try {
        const result = await uploadImage(data).unwrap();
        setAttachment(result.data.Profileimage);
        setImageLoading(false);
      } catch (e: any) {}
    }
  };

  const pathname = attachment ? new URL(attachment).pathname : "";

  const fileName = pathname.split("/").pop();

  const latestTransactionId =
    pettyCashState.ledger.length > 0
      ? pettyCashState.ledger[pettyCashState.ledger.length - 1].transactionId
      : "TRS000001";

  const newTransactionId = latestTransactionId
    ? latestTransactionId.split("TRS").pop()
    : "000001";

  const newTransactionIdNumber = newTransactionId
    ? parseInt(newTransactionId) + 1
    : "000001";

  const newTransactionIdString = newTransactionIdNumber.toString();

  const finalTransactionId = "TRS" + newTransactionIdString.padStart(6, "0");

  const accountList: AccountChart[] = accounts.accountChartsList;

  const validationSchema = Yup.object().shape({
    incomeType: Yup.string().required("Income type is required"),
    incomeAmount: Yup.string().required("Income amount is required"),
    incomeDate: Yup.string().required("Income date is required"),
    accountNo: Yup.string().required("Bank account is required"),
  });

  return (
    <div className="user-list mt-2 section px-4 py-4">
      <Content />
      <ToastErrorComponent
        show={show}
        setShow={setShow}
        content={errorContent}
      />
      <Row>
        <Col sm="12">
          <div className="section-heading ">
            <div className="d-flex align-items-center gap-4">
              <button
                className="btn border-0 p-0"
                onClick={() => {
                  navigate(-1);
                }}
                type="button"
              >
                {" "}
                <img src={Common.ArrowLeftBlue} alt="next" />
              </button>
              <h4 className="sub-heading mb-0" style={{ fontSize: "22px" }}>
                Add Credit Petty Cash
              </h4>
            </div>
            <div className="transaction-id mt-4 d-flex align-items-center gap-2 mb-4">
              <h4 className="sub-heading normal-font-weight textBlack">
                Transaction ID:
              </h4>
              <h4 className="sub-heading normal-font-weight">
                {finalTransactionId}
              </h4>
            </div>
          </div>
        </Col>
        <Col sm="12">
          <Formik
            initialValues={initialValues}
            onSubmit={(values) => {
              handleSubmit(values);
            }}
            validationSchema={validationSchema}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
            }) => (
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col sm="4">
                    <Form.Group className="mb-3" controlId="formBasicEmail">
                      <h4
                        className="sub-heading normal-font-weight textBlack"
                        style={{ fontSize: "18px" }}
                      >
                        Income Type *
                      </h4>
                      <Form.Control
                        type="text"
                        name="incomeType"
                        placeholder="Enter income type"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.incomeType}
                        className={
                          touched.incomeType && errors.incomeType
                            ? "is-invalid custom-input border-fill"
                            : "custom-input border-fill"
                        }
                      />
                      {touched.incomeType && errors.incomeType ? (
                        <div className="invalid-feedback">
                          {errors.incomeType}
                        </div>
                      ) : null}
                    </Form.Group>
                  </Col>
                  <Col sm="4">
                    <Form.Group className="mb-4" controlId="formBasicEmail">
                      <h4
                        className="sub-heading normal-font-weight textBlack mb-2"
                        style={{ fontSize: "18px" }}
                      >
                        Income Amount *
                      </h4>
                      <Form.Group className="position-relative input-design d-flex align-items-center">
                        <Form.Control
                          type="text"
                          name="incomeAmount"
                          onChange={(e) => {
                            // Accept numbers and float values
                            const re = /^[0-9\b]*(\.\d{0,2})?$/;

                            if (
                              e.target.value === "" ||
                              re.test(e.target.value)
                            ) {
                              handleChange(e);
                            }
                          }}
                          onBlur={handleBlur}
                          placeholder="Enter income amount"
                          className="custom-input"
                          value={values.incomeAmount}
                        />
                        SAR
                      </Form.Group>
                      {touched.incomeAmount && errors.incomeAmount ? (
                        <div className="invalid-feedback">
                          {errors.incomeAmount}
                        </div>
                      ) : null}
                    </Form.Group>
                  </Col>
                  <Col sm="4">
                    <Form.Group className="mb-4" controlId="formBasicEmail">
                      <h4
                        className="sub-heading normal-font-weight textBlack"
                        style={{ fontSize: "18px" }}
                      >
                        Income Date *
                      </h4>
                      <Form.Group className="d-flex align-items-center input-design position-relative w-100">
                        <DatePicker
                          name="incomeDate"
                          selected={
                            values.incomeDate
                              ? new Date(values.incomeDate)
                              : null
                          }
                          onChange={(date: any) =>
                            handleChange({
                              target: { name: "incomeDate", value: date },
                            })
                          }
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          autoComplete="off"
                          maxDate={new Date()}
                          placeholderText="Income Date"
                          className={
                            errors.incomeDate && touched.incomeDate
                              ? "is-invalid w-100 custom-input"
                              : "w-100 custom-input"
                          }
                          dateFormat="yyyy-MM-d"
                        />
                        <img
                          src={Common.Calendar}
                          className="calendar-date-recur"
                          alt="calendarblack"
                        />
                      </Form.Group>
                      {touched.incomeDate && errors.incomeDate ? (
                        <div className="invalid-feedback">
                          {errors.incomeDate}
                        </div>
                      ) : null}
                    </Form.Group>
                  </Col>
                  <Col sm="8">
                    <Form.Group className="mb-3" controlId="formBasicEmail">
                      <h4
                        className="sub-heading normal-font-weight textBlack"
                        style={{ fontSize: "18px" }}
                      >
                        Income Detail
                      </h4>
                      <textarea
                        name="incomeDetail"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.incomeDetail}
                        placeholder="Leave a comment here..."
                        className={
                          touched.incomeDetail && errors.incomeDetail
                            ? "is-invalid border-fill w-100"
                            : "border-fill w-100"
                        }
                        rows={6}
                        style={{ resize: "none" }}
                      />
                      {touched.incomeDetail && errors.incomeDetail ? (
                        <div className="invalid-feedback">
                          {errors.incomeDetail}
                        </div>
                      ) : null}
                    </Form.Group>
                  </Col>
                  <Col sm="4">
                    <Form.Group className="mb-3" controlId="formBasicEmail">
                      <h4
                        className="sub-heading normal-font-weight textBlack"
                        style={{ fontSize: "18px" }}
                      >
                        Bank Account *
                      </h4>
                      <Form.Select
                        name="accountNo"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.accountNo}
                        className={
                          touched.accountNo && errors.accountNo
                            ? "is-invalid custom-input border-fill"
                            : "custom-input border-fill"
                        }
                      >
                        <option value="">Select bank account</option>
                        {accountList.map((method, index) => (
                          <option key={index} value={method.accountId}>
                            {method.accountName}
                          </option>
                        ))}
                      </Form.Select>

                      {touched.accountNo && errors.accountNo ? (
                        <div className="invalid-feedback">
                          {errors.accountNo}
                        </div>
                      ) : null}
                    </Form.Group>
                  </Col>
                  <Col sm="4">
                    <Form.Group className="mb-4">
                      <h4
                        className="sub-heading normal-font-weight textBlack"
                        style={{ fontSize: "18px" }}
                      >
                        Attachment
                      </h4>
                      <Form.Label className="profile-image-label cursor-pointer d-flex align-items-center justify-content-center">
                        <Form.Control
                          type="file"
                          name="attachment"
                          onChange={handleUploadImage}
                          accept=".pdf, .jpg, .jpeg, .png"
                          className="d-none"
                          multiple={false}
                        />
                        <div className="text-center">
                          <div className="add-profile-image d-flex align-items-center justify-content-center mx-auto">
                            <img src={EmployeeIcons.Plus} alt="plus" />
                          </div>
                          {attachment ? (
                            <p
                              className="upload-para mt-2 mb-0"
                              style={{ fontSize: "14px" }}
                            >
                              Change Attachment
                            </p>
                          ) : null}
                          <p
                            className="upload-para mt-2 mb-0"
                            style={{ fontSize: "14px" }}
                          >
                            Upload JPG, JPEG, PNG & PDF File only
                          </p>
                          <p className="upload-para mt-0 mb-0">
                            Max file size is 100MB
                          </p>
                        </div>
                      </Form.Label>
                    </Form.Group>
                    <div>
                      {imageLoading ? (
                        <LoaderBlue height={40} width={40} />
                      ) : attachment ? (
                        <h4 className="sub-heading">{fileName}</h4>
                      ) : null}
                    </div>
                  </Col>
                  <Col sm="12">
                    <div className="text-end mt-4 d-flex align-items-center gap-4">
                      <button
                        className="btn primary-button normal-font-weight"
                        type="button"
                        onClick={handleAddLanguageSet}
                      >
                        <img
                          src={Common.Plus}
                          width="20px"
                          height="20px"
                          className="me-2"
                          alt="next"
                        />{" "}
                        Approval user
                      </button>
                    </div>
                  </Col>
                  <Col sm="12">
                    <div className="extra-detail my-4">
                      {languageSets.length > 0 && (
                        <h4 className="sub-heading normal-font-weight mb-4">
                          Approval will be sent to these users:
                        </h4>
                      )}
                      {languageSets}
                    </div>
                  </Col>
                  <Col sm="12">
                    <div className="d-flex justify-content-end mt-4">
                      <button type="submit" className="btn primary-button w-25">
                        {pettyCashState.addCreditLoading ? (
                          <LoaderWhite height={30} width={30} />
                        ) : (
                          "Add Amount"
                        )}
                      </button>
                    </div>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    </div>
  );
};
export default CreditPettyCash;
